/* eslint-disable @typescript-eslint/no-explicit-any */

/** The separators a single field uses when it carries several values. */
export const optionsDelimiters = [';'];

/**
 * Reads a number off a value that may arrive formatted, so `'$1,250'` compares as 1250.
 */
const stringToNumber = (value: string | number | null | undefined): number | null => {
  if (value == null) return null;
  if (typeof value !== 'string') return value;
  return parseFloat(value.replace(/,|\$|-/g, ''));
};

/**
 * Tests if a target value matches a text search term. The search is case-insensitive on both
 * sides — a caller that hands over the term as the user typed it still gets a match.
 */
const matchesTextFilter = (targetValue: any, searchName: string, includeBlank: boolean = false): boolean => {
  if (!targetValue) return includeBlank;

  const term = searchName.toLowerCase();

  // Tested before stringifying, so a multi-value field matches on one element rather than on the
  // punctuation JSON.stringify would put between them.
  if (Array.isArray(targetValue)) {
    return targetValue.some((tv) => {
      if (tv === undefined || tv === null) return includeBlank;
      const stringVal = JSON.stringify(tv).toLowerCase();
      return stringVal.includes(term);
    });
  }

  const stringVal = JSON.stringify(targetValue).toLowerCase();
  return stringVal.includes(term);
};

/**
 * Tests if a target value falls within a numeric range
 */
const matchesRangeFilter = (
  targetValue: any,
  minValue: number,
  maxValue: number,
  includeBlank: boolean = false,
): boolean => {
  if (Array.isArray(targetValue)) {
    const targetNumbers = targetValue.map((tv) => stringToNumber(tv));
    return targetNumbers.some((tv) => {
      return tv != null ? tv >= minValue && tv <= maxValue : includeBlank;
    });
  }
  const targetNumber = stringToNumber(targetValue);
  return targetNumber != null ? targetNumber >= minValue && targetNumber <= maxValue : includeBlank;
};

/** Tests if a target value matches any selected autocomplete items */
const matchesAutocompleteFilter = (targetValue: any, selectedItems: string[]): boolean => {
  if (Array.isArray(targetValue)) {
    return targetValue.some((tv) => selectedItems.includes(tv));
  }
  return selectedItems.includes(targetValue);
};

/**
 * Splits a field that carries several values into those values, so an exact-match filter can test
 * each one. Anything that is not a delimited string is returned untouched.
 */
const splitMultiValue = (value: any): any => {
  if (typeof value !== 'string') return value;
  for (const delimiter of optionsDelimiters) {
    if (value.includes(delimiter)) {
      return value.split(delimiter).map((part) => part.trim());
    }
  }
  return value;
};

export {
  stringToNumber,
  matchesTextFilter,
  matchesRangeFilter,
  matchesAutocompleteFilter,
  splitMultiValue,
};
